import { Injectable, signal } from '@angular/core';

/** Lo que escribe `desplegar.sh` en el artefacto, justo antes de subirlo. */
export interface VersionDelArtefacto {
  sha: string;
  corto?: string;
  rama?: string;
  entorno?: string;
  subidoEn?: string;
}

/** Lo que se pinta. `ultimaConocida` avisa de que es de un rato antes, no de lo que hay servido ahora. */
export interface VersionVista {
  corto: string | null;
  cuando: string | null;
  ultimaConocida: boolean;
}

export const SIN_VERSION: VersionVista = { corto: null, cuando: null, ultimaConocida: false };

/**
 * De qué versión es el POS que se está usando, para que al reportar algo se sepa qué se estaba viendo.
 *
 * Tres cosas medidas que mandan sobre cómo está escrito esto:
 *
 * 1. **El código de respuesta no sirve.** En staging, `/version.json` y una ruta inventada devuelven **las dos 200
 *    con el `index.html`** (medido por Red el 2026-09-16). Así que se comprueba el contenido: un objeto con `sha`
 *    de verdad; cualquier otra cosa es «no se pudo leer». Fiarse del 200 enseñaría «Versión <!doctype html>».
 * 2. **Sin caché.** Se pide con `cache: 'no-store'`: una copia del navegador enseñaría con toda confianza la versión
 *    del despliegue anterior, que es justo el error que esto viene a evitar.
 * 3. **Nunca un número inventado.** Si nunca se pudo leer, se dice «sin versión»; jamás `0.0.0`, que parece un dato.
 *
 * Y el fichero queda **fuera del manifiesto del service worker** (hay una prueba que falla si entra): dentro, el POS
 * enseñaría la versión de la instalación anterior sin que nada avisara.
 */
@Injectable({ providedIn: 'root' })
export class VersionServidaService {
  /** Por usuario y equipo: dos personas en el mismo navegador no comparten «la última que yo vi». */
  static clave(usuario: string | null): string {
    return `suresell.version.ultima.${usuario ?? 'sin-usuario'}`;
  }

  readonly version = signal<VersionVista>(SIN_VERSION);
  readonly leyendo = signal(false);

  /** Lee la versión servida. Nunca lanza: esto es un adorno honesto, no una función de la caja. */
  async leer(usuario: string | null = null): Promise<VersionVista> {
    this.leyendo.set(true);
    try {
      const datos = await this.pedir();
      if (datos) {
        const vista: VersionVista = {
          corto: datos.corto ?? datos.sha.slice(0, 7),
          // Si el artefacto no trae `subidoEn` (lo escribe `desplegar.sh`, pero un fichero a mano podría no
          // traerlo), vale el momento en que se leyó: la condición de ECM es que la última conocida diga SIEMPRE de
          // cuándo es. «Última conocida» sin fecha no dice nada: podría ser de hace un mes.
          cuando: datos.subidoEn ?? new Date().toISOString(),
          ultimaConocida: false,
        };
        this.guardar(usuario, vista);
        this.version.set(vista);
        return vista;
      }
      const guardada = this.ultimaConocida(usuario);
      this.version.set(guardada);
      return guardada;
    } finally {
      this.leyendo.set(false);
    }
  }

  /** Lo guardado la última vez que sí se pudo leer, marcado como tal. Sin nada, «sin versión». */
  ultimaConocida(usuario: string | null): VersionVista {
    try {
      const crudo = localStorage.getItem(VersionServidaService.clave(usuario));
      if (!crudo) return SIN_VERSION;
      const guardada = JSON.parse(crudo) as { corto?: unknown; cuando?: unknown };
      if (typeof guardada?.corto !== 'string' || !guardada.corto) return SIN_VERSION;
      return {
        corto: guardada.corto,
        cuando: typeof guardada.cuando === 'string' ? guardada.cuando : null,
        ultimaConocida: true,
      };
      // (si lo guardado no trae fecha —de una versión anterior de este código— se dice sin ella, que es peor pero
      //  honesto; lo nuevo siempre la guarda)
    } catch {
      return SIN_VERSION;
    }
  }

  private guardar(usuario: string | null, vista: VersionVista): void {
    try {
      localStorage.setItem(
        VersionServidaService.clave(usuario),
        JSON.stringify({ corto: vista.corto, cuando: vista.cuando }),
      );
    } catch {
      /* almacenamiento no disponible: se enseña lo leído y no se recuerda */
    }
  }

  /** El GET, con lo suyo: sin caché, y validando el CONTENIDO porque el código de respuesta miente. */
  private async pedir(): Promise<VersionDelArtefacto | null> {
    try {
      const respuesta = await fetch('/version.json', { cache: 'no-store' });
      if (!respuesta.ok) return null;
      const texto = await respuesta.text();
      const datos = JSON.parse(texto) as unknown; // un `index.html` revienta aquí, que es lo que se quiere
      if (!datos || typeof datos !== 'object') return null;
      const sha = (datos as { sha?: unknown }).sha;
      if (typeof sha !== 'string' || !sha) return null;
      return datos as VersionDelArtefacto;
    } catch {
      // Sin red, JSON ilegible o el index.html disfrazado de 200: las tres son «no se pudo leer».
      return null;
    }
  }
}

/** «16/09 10:42» en Bogotá. Vacío si no hay fecha. */
export function cuandoCorto(iso: string | null): string {
  if (!iso) return '';
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return '';
  const partes = new Intl.DateTimeFormat('es-CO', {
    timeZone: 'America/Bogota', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(fecha);
  // Se rellena a dos cifras aquí: con es-CO y estas opciones, `2-digit` devuelve «9» y no «09» (medido).
  const de = (tipo: string) => (partes.find((p) => p.type === tipo)?.value ?? '').padStart(2, '0');
  return `${de('day')}/${de('month')} ${de('hour')}:${de('minute')}`;
}

/** El texto entero, en las palabras que aprobó ECM. */
export function textoDeLaVersion(vista: VersionVista): string {
  if (!vista.corto) return 'sin versión';
  const cuando = cuandoCorto(vista.cuando);
  // «Compilación» (ECM y Líder, 27/09): «Versión» es la del producto (3.0.0); esta línea es el build exacto que corre.
  if (vista.ultimaConocida) return `Compilación ${vista.corto} · última conocida${cuando ? `, del ${cuando}` : ''}`;
  return cuando ? `Compilación ${vista.corto} · ${cuando}` : `Compilación ${vista.corto}`;
}
